/**
 * Transport for the roofing platform API.
 *
 * Methods are addressed as POST /<proto package>.<Service>/<Method> with a
 * JSON body, matching the backend routing convention. Request and response
 * shapes come from the generated contract types, so the interface never
 * declares its own copy of a domain model.
 */

import type {
  ArchiveProjectRequest,
  ExportDrawingRequest,
  ExportDrawingResponse,
  GenerateWindZonesRequest,
  GenerateWindZonesResponse,
  ValidateRoofGeometryRequest,
  ValidateRoofGeometryResponse,
  ValidateSectionProfileRequest,
  ValidateSectionProfileResponse,
  ArchiveProjectResponse,
  CalculationRequest,
  CalculationResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  GetCapabilitiesRequest,
  GetCapabilitiesResponse,
  GetProjectRequest,
  GetProjectResponse,
  ListProjectsRequest,
  ListProjectsResponse,
  UpdateProjectRequest,
  UpdateProjectResponse,
  ListAccessoriesRequest,
  ListAccessoriesResponse,
  ListCatalogueVersionsRequest,
  ListCatalogueVersionsResponse,
  ListCompatibleFixingSystemsRequest,
  ListCompatibleFixingSystemsResponse,
  ListCompatibleMembranesRequest,
  ListCompatibleMembranesResponse,
  ListSteelDeckProfilesRequest,
  ListSteelDeckProfilesResponse,
  ValidateCustomProductRequest,
  ValidateCustomProductResponse,
  GetETAReferenceCatalogueRequest,
  GetETAReferenceCatalogueResponse,
  ListNationalWindDatasetManifestsRequest,
  ListNationalWindDatasetManifestsResponse,
  GetNationalWindDatasetManifestRequest,
  GetNationalWindDatasetManifestResponse,
  ListSelectableNationalDatasetsRequest,
  ListSelectableNationalDatasetsResponse,
  ListWindEngineeringRulePackagesRequest,
  ListWindEngineeringRulePackagesResponse,
  GetWindEngineeringRulePackageRequest,
  GetWindEngineeringRulePackageResponse,
  CheckWindEngineeringActivationRequest,
  CheckWindEngineeringActivationResponse,
  GetReportStatusRequest,
  GetReportStatusResponse,
  ReportRequest,
  ReportResponse,
} from '../gen/ejot/roofing/v1/types';
import { MethodPaths } from '../gen/ejot/roofing/v1/types';

const baseUrl = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/**
 * The credential sent with every request.
 *
 * It is held in memory rather than in storage. A token in local storage is
 * readable by any script that reaches the page and survives the tab being
 * closed, which is exactly the combination that makes one worth stealing.
 *
 * Nothing sets this yet: the deployment's identity service is not specified,
 * and the development authentication mode accepts every request. The seam is
 * here so that wiring one in is a single call rather than a change to every
 * request path.
 */
let credential: string | undefined;

/** Sets the bearer credential sent with subsequent requests. */
export function setCredential(token: string | undefined): void {
  credential = token;
}

function authorizationHeaders(): Record<string, string> {
  return credential ? { Authorization: `Bearer ${credential}` } : {};
}

/** An error carrying the status and message the backend reported. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface ErrorBody {
  code?: string;
  message?: string;
}

/**
 * Calls one unary method.
 *
 * The abort signal is passed through so that a superseded request can be
 * cancelled: geometry and calculation requests are expensive, and a stale
 * response must never overwrite a newer one.
 */
async function call<Req, Res>(path: string, request: Req, signal?: AbortSignal): Promise<Res> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authorizationHeaders() },
    body: JSON.stringify(request ?? {}),
    signal,
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorBody;
      if (body.message) message = body.message;
    } catch {
      // The body was not the expected error shape; the status line stands.
    }
    throw new ApiError(response.status, message);
  }

  return (await response.json()) as Res;
}

export const platformService = {
  getCapabilities(
    request: GetCapabilitiesRequest = {},
    signal?: AbortSignal,
  ): Promise<GetCapabilitiesResponse> {
    return call(MethodPaths.PlatformServiceGetCapabilities, request, signal);
  },
};

export const projectService = {
  createProject(request: CreateProjectRequest, signal?: AbortSignal): Promise<CreateProjectResponse> {
    return call(MethodPaths.ProjectServiceCreateProject, request, signal);
  },
  getProject(request: GetProjectRequest, signal?: AbortSignal): Promise<GetProjectResponse> {
    return call(MethodPaths.ProjectServiceGetProject, request, signal);
  },
  updateProject(request: UpdateProjectRequest, signal?: AbortSignal): Promise<UpdateProjectResponse> {
    return call(MethodPaths.ProjectServiceUpdateProject, request, signal);
  },
  listProjects(request: ListProjectsRequest = {}, signal?: AbortSignal): Promise<ListProjectsResponse> {
    return call(MethodPaths.ProjectServiceListProjects, request, signal);
  },
  archiveProject(request: ArchiveProjectRequest, signal?: AbortSignal): Promise<ArchiveProjectResponse> {
    return call(MethodPaths.ProjectServiceArchiveProject, request, signal);
  },
};

export const geometryService = {
  /**
   * Validates a roof plan.
   *
   * The backend is authoritative for validity, area and perimeter. The editor
   * renders an immediate preview while drawing, but what it reports comes from
   * here, so the drawing and the calculation cannot disagree.
   */
  validateRoofGeometry(
    request: ValidateRoofGeometryRequest, signal?: AbortSignal,
  ): Promise<ValidateRoofGeometryResponse> {
    return call(MethodPaths.GeometryServiceValidateRoofGeometry, request, signal);
  },
  generateWindZones(
    request: GenerateWindZonesRequest, signal?: AbortSignal,
  ): Promise<GenerateWindZonesResponse> {
    return call(MethodPaths.GeometryServiceGenerateWindZones, request, signal);
  },
  /**
   * Validates a roof build-up and returns its derived dimensions.
   *
   * The total insulation thickness and the required fastener length come back
   * from here rather than being summed in the browser: the fastener length is
   * what fixing-combination selection is filtered by.
   */
  validateSectionProfile(
    request: ValidateSectionProfileRequest, signal?: AbortSignal,
  ): Promise<ValidateSectionProfileResponse> {
    return call(MethodPaths.GeometryServiceValidateSectionProfile, request, signal);
  },
  /**
   * Serialises a backend drawing scene to CAD.
   *
   * The scene sent here is one the backend produced. The browser never
   * assembles drawing geometry of its own to export: the DXF, the PDF page and
   * the canvas have to be the same drawing, not three that agree by luck.
   */
  exportDrawing(
    request: ExportDrawingRequest, signal?: AbortSignal,
  ): Promise<ExportDrawingResponse> {
    return call(MethodPaths.GeometryServiceExportDrawing, request, signal);
  },
};

export const calculationService = {
  /**
   * Requests a calculation for one module.
   *
   * The response is authoritative. The interface renders it and never
   * recomputes, adjusts or infers any engineering value from it.
   */
  calculateModule(request: CalculationRequest, signal?: AbortSignal): Promise<CalculationResponse> {
    return call(MethodPaths.CalculationServiceCalculateModule, request, signal);
  },
  calculateProject(request: CalculationRequest, signal?: AbortSignal): Promise<CalculationResponse> {
    return call(MethodPaths.CalculationServiceCalculateProject, request, signal);
  },
};

export const catalogueService = {
  listCatalogueVersions(
    request: ListCatalogueVersionsRequest = {} as ListCatalogueVersionsRequest,
    signal?: AbortSignal,
  ): Promise<ListCatalogueVersionsResponse> {
    return call(MethodPaths.CatalogueServiceListCatalogueVersions, request, signal);
  },
  listCompatibleMembranes(
    request: ListCompatibleMembranesRequest, signal?: AbortSignal,
  ): Promise<ListCompatibleMembranesResponse> {
    return call(MethodPaths.CatalogueServiceListCompatibleMembranes, request, signal);
  },
  /**
   * Lists the fixing systems the current selections permit.
   *
   * The exclusions come back with the list, not instead of it. Which products
   * are compatible is a catalogue decision, and the interface displays the
   * answer and the reason rather than working either out for itself.
   */
  listCompatibleFixingSystems(
    request: ListCompatibleFixingSystemsRequest, signal?: AbortSignal,
  ): Promise<ListCompatibleFixingSystemsResponse> {
    return call(MethodPaths.CatalogueServiceListCompatibleFixingSystems, request, signal);
  },
  listAccessories(
    request: ListAccessoriesRequest, signal?: AbortSignal,
  ): Promise<ListAccessoriesResponse> {
    return call(MethodPaths.CatalogueServiceListAccessories, request, signal);
  },
  listSteelDeckProfiles(
    request: ListSteelDeckProfilesRequest = {}, signal?: AbortSignal,
  ): Promise<ListSteelDeckProfilesResponse> {
    return call(MethodPaths.CatalogueServiceListSteelDeckProfiles, request, signal);
  },
  /**
   * Checks a product the user entered by hand.
   *
   * The check is the backend's, including the consistency rules. Nothing here
   * decides whether a hand-entered product is usable.
   */
  validateCustomProduct(
    request: ValidateCustomProductRequest, signal?: AbortSignal,
  ): Promise<ValidateCustomProductResponse> {
    return call(MethodPaths.CatalogueServiceValidateCustomProduct, request, signal);
  },
  getETAReferenceCatalogue(
    request: GetETAReferenceCatalogueRequest = {}, signal?: AbortSignal,
  ): Promise<GetETAReferenceCatalogueResponse> {
    return call(MethodPaths.CatalogueServiceGetETAReferenceCatalogue, request, signal);
  },
};

export const windDataService = {
  listNationalWindDatasetManifests(
    request: ListNationalWindDatasetManifestsRequest = {}, signal?: AbortSignal,
  ): Promise<ListNationalWindDatasetManifestsResponse> {
    return call(MethodPaths.WindDataServiceListNationalWindDatasetManifests, request, signal);
  },
  getNationalWindDatasetManifest(
    request: GetNationalWindDatasetManifestRequest, signal?: AbortSignal,
  ): Promise<GetNationalWindDatasetManifestResponse> {
    return call(MethodPaths.WindDataServiceGetNationalWindDatasetManifest, request, signal);
  },
  listSelectableNationalDatasets(
    request: ListSelectableNationalDatasetsRequest = {}, signal?: AbortSignal,
  ): Promise<ListSelectableNationalDatasetsResponse> {
    return call(MethodPaths.WindDataServiceListSelectableNationalDatasets, request, signal);
  },
};

export const engineeringRuleService = {
  listWindEngineeringRulePackages(
    request: ListWindEngineeringRulePackagesRequest = {}, signal?: AbortSignal,
  ): Promise<ListWindEngineeringRulePackagesResponse> {
    return call(MethodPaths.EngineeringRuleServiceListWindEngineeringRulePackages, request, signal);
  },
  getWindEngineeringRulePackage(
    request: GetWindEngineeringRulePackageRequest, signal?: AbortSignal,
  ): Promise<GetWindEngineeringRulePackageResponse> {
    return call(MethodPaths.EngineeringRuleServiceGetWindEngineeringRulePackage, request, signal);
  },
  checkWindEngineeringActivation(
    request: CheckWindEngineeringActivationRequest, signal?: AbortSignal,
  ): Promise<CheckWindEngineeringActivationResponse> {
    return call(MethodPaths.EngineeringRuleServiceCheckWindEngineeringActivation, request, signal);
  },
};

export const reportService = {
  /**
   * Generates a report from an already-computed result.
   *
   * The result is sent with the request rather than recomputed by the report
   * engine. That is what makes the report and the interface show the same
   * numbers: they are the same numbers, not two derivations of them.
   */
  generateReport(request: ReportRequest, signal?: AbortSignal): Promise<ReportResponse> {
    return call(MethodPaths.ReportServiceGenerateReport, request, signal);
  },
  getReportStatus(
    request: GetReportStatusRequest, signal?: AbortSignal,
  ): Promise<GetReportStatusResponse> {
    return call(MethodPaths.ReportServiceGetReportStatus, request, signal);
  },
  /**
   * Fetches a generated report file.
   *
   * The file is fetched rather than linked to. A plain anchor is a browser
   * navigation, and a navigation cannot carry the credential the report route
   * requires — so a link works only against a deployment that authenticates
   * nothing, which production does not.
   *
   * The caller owns the returned object URL and must revoke it; a blob held
   * open for the life of the page keeps the whole file in memory.
   */
  async downloadReport(artifactId: string, signal?: AbortSignal): Promise<Blob> {
    const response = await fetch(`${baseUrl}/reports/${encodeURIComponent(artifactId)}`, {
      headers: authorizationHeaders(),
      signal,
    });
    if (!response.ok) {
      throw new ApiError(response.status, `the report could not be downloaded (${response.status})`);
    }
    return response.blob();
  },
};